import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../services/api';
import { Search, RefreshCw, Eye } from 'lucide-react';
import LogBodyDetailModal from './LogBodyDetailModal';

// ── Shared badge (same class pattern as Analytics.js) ─────────────────────────
const LevelBadge = ({ level }) => {
  const levelClass = `level-${level?.toLowerCase()}`;
  return <span className={`badge ${levelClass}`}>{level}</span>;
};

/**
 * Shows a human-friendly relative time string ("2m ago", "1h ago", "3d ago")
 * with a tooltip showing the full locale timestamp.
 */
const formatRelativeTime = (timestamp) => {
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  if (diffSecs < 60)  return `${diffSecs}s ago`;
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60)  return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

/**
 * Renders at most 3 key:value tag chips from a tags object.
 * Remaining tags are summarised as "+N".
 */
const TagChips = ({ tags }) => {
  if (!tags || typeof tags !== 'object') return null;
  const entries = Object.entries(tags).slice(0, 3);
  if (entries.length === 0) return null;
  const remaining = Object.keys(tags).length - 3;
  return (
    <div className="tag-chips">
      {entries.map(([k, v]) => (
        <span key={k} className="tag-chip" title={`${k}: ${v}`}>
          {k}: {v}
        </span>
      ))}
      {remaining > 0 && (
        <span className="tag-chip tag-chip-more">+{remaining}</span>
      )}
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────────

const LogList = ({ selectedApp, workerApiUrl, onBackToApps, embedded = false }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [selectedLogForBodyView, setSelectedLogForBodyView] = useState(null);

  /**
   * Server-side level filtering: the `level` param is forwarded to the API
   * so the filter applies across ALL logs, not just the loaded page.
   * `fetchLogs` reference changes whenever `levelFilter` changes (it's in
   * the dependency array), which causes the useEffect below to reset and
   * re-fetch automatically.
   */
  const fetchLogs = useCallback(async (newCursor = null) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getLogs(workerApiUrl, {
        appId: selectedApp.id,
        cursor: newCursor,
        limit: 50,
        ...(levelFilter !== 'all' && { level: levelFilter }),
      });
      setLogs(prev => newCursor ? [...prev, ...data.logs] : data.logs);
      setCursor(data.nextCursor);
      setHasMore(!!data.nextCursor);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workerApiUrl, selectedApp.id, levelFilter]);

  useEffect(() => {
    // When fetchLogs changes (i.e. levelFilter changed), clear the list and
    // start from the beginning so we don't mix results from different filters.
    setLogs([]);
    setCursor(null);
    setHasMore(true);
    fetchLogs(null);
  }, [fetchLogs]);

  // Client-side search applied on top of the server-side level filter.
  // Searches message, template name, category, and tag values.
  const filteredLogs = logs.filter(log => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      log.message?.toLowerCase().includes(q) ||
      log.template_name?.toLowerCase().includes(q) ||
      log.category?.toLowerCase().includes(q) ||
      (log.tags && Object.values(log.tags).some(v => String(v).toLowerCase().includes(q)))
    );
  });

  const handleRefresh = () => {
    setLogs([]);
    setCursor(null);
    setHasMore(true);
    fetchLogs(null);
  };

  return (
    <div className={`log-view ${embedded ? 'embedded' : ''}`}>

      {/* Optional breadcrumb when used standalone (not embedded in AppDetailView) */}
      {!embedded && onBackToApps && (
        <div className="sub-header">
          <div className="breadcrumbs">
            <span onClick={onBackToApps} className="breadcrumb-link">Apps</span>
            {' '}&gt;{' '}
            <span>{selectedApp.name}</span>
          </div>
        </div>
      )}

      {/* ── Controls ──────────────────────────────────────────────── */}
      <div className="logs-controls">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search message, template, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <select
            value={levelFilter}
            onChange={(e) => setLevelFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Levels</option>
            <option value="info">Info</option>
            <option value="warn">Warn</option>
            <option value="error">Error</option>
            <option value="fatal">Fatal</option>
            <option value="debug">Debug</option>
          </select>
          <button className="refresh-btn" onClick={handleRefresh}>
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* ── Table ─────────────────────────────────────────────────── */}
      <div className="logs-table-container">
        <div className="table-container-inner">
          <table className="logs-table realtime-logs">
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Message</th>
                <th>Template</th>
                <th>Tags</th>
                <th>Body</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                <tr><td colSpan="6">Loading logs...</td></tr>
              ) : error ? (
                <tr><td colSpan="6" className="error">{error}</td></tr>
              ) : filteredLogs.length > 0 ? (
                filteredLogs.map(log => (
                  <tr key={log.id} className={`log-row log-${log.level?.toLowerCase()}`}>

                    {/* Relative timestamp; hover shows full date/time */}
                    <td className="log-timestamp">
                      <span title={new Date(log.timestamp).toLocaleString()}>
                        {formatRelativeTime(log.timestamp)}
                      </span>
                    </td>

                    <td>
                      <LevelBadge level={log.level} />
                    </td>

                    {/* Plain truncated text — tooltip reveals full message on hover */}
                    <td className="log-message">
                      <span title={log.message}>{log.message || 'No message'}</span>
                    </td>

                    {/* Template name replaces the old fake "HTTP method + endpoint" column */}
                    <td className="log-template">
                      <span className="template-name" title={log.template_name}>
                        {log.template_name || '—'}
                      </span>
                    </td>

                    <td className="log-tags">
                      <TagChips tags={log.tags} />
                    </td>

                    <td className="log-actions">
                      {log.has_body && (
                        <button
                          className="icon-btn"
                          onClick={() => setSelectedLogForBodyView(log)}
                          title="View log body"
                        >
                          <Eye size={16} />
                        </button>
                      )}
                    </td>

                  </tr>
                ))
              ) : (
                <tr><td colSpan="6">No logs found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && !loading && (
          <div className="load-more-container">
            <button className="btn-secondary" onClick={() => fetchLogs(cursor)}>
              Load More
            </button>
          </div>
        )}
      </div>

      {selectedLogForBodyView && (
        <LogBodyDetailModal
          log={selectedLogForBodyView}
          workerApiUrl={workerApiUrl}
          onClose={() => setSelectedLogForBodyView(null)}
        />
      )}
    </div>
  );
};

export default LogList;
